import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MemberCard } from '@/components/network/MemberCard';
import { useProfilesPaged } from '@/hooks/useProfile';
import { useCohorts } from '@/hooks/useCohorts';
import { Search, Filter, Loader2, Users, X } from 'lucide-react';
import { INDUSTRY_PRESETS } from '@/lib/onboarding-presets';
import { useBlocks } from '@/hooks/useBlocks';

export default function DirectoryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mentorsOnly, setMentorsOnly] = useState(false);
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const { data: cohorts } = useCohorts();
  const [page, setPage] = useState(0);
  const pageSize = 24;

  const {
    data: profilesPage,
    isLoading,
    isError,
    error,
  } = useProfilesPaged({
    page,
    pageSize,
    search: searchQuery || undefined,
    isMentor: mentorsOnly || undefined,
    cityCohorts: selectedCohorts.length > 0 ? selectedCohorts : undefined,
    industry: selectedIndustry || undefined,
  });
  const { data: blocks } = useBlocks();
  const blockedIds = new Set(blocks?.map((b) => b.blocked_id) || []);

  const filteredProfiles = profilesPage?.data?.filter((profile) => !blockedIds.has(profile.user_id)) || [];
  const totalCount = profilesPage?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const activeFiltersCount = [
    mentorsOnly,
    selectedCohorts.length > 0,
    selectedIndustry,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setMentorsOnly(false);
    setSelectedCohorts([]);
    setSelectedIndustry('');
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(Math.min(Math.max(nextPage, 0), totalPages - 1));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Directory</h1>
        <p className="text-muted-foreground text-sm">
          Find and connect with fellow QBN members
        </p>
      </div>

      {/* Search & Filter Toggle */}
      <div className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, company, or role..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              className="pl-10"
            />
          </div>
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Filters</h3>
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-auto p-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear all
                </Button>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Cohort Filter */}
              <div className="space-y-2">
                <Label>City Cohort</Label>
                <div className="flex flex-wrap gap-1.5">
                  {cohorts?.map((cohort) => {
                    const isSelected = selectedCohorts.includes(cohort.name);
                    return (
                      <button
                        key={cohort.id}
                        onClick={() => {
                          setSelectedCohorts((prev) =>
                            isSelected
                              ? prev.filter((c) => c !== cohort.name)
                              : [...prev, cohort.name]
                          );
                          setPage(0);
                        }}
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                          isSelected
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        {cohort.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Industry Filter */}
              <div className="space-y-2">
                <Label>Industry</Label>
                <Select
                  value={selectedIndustry || '__all__'}
                  onValueChange={(value) => {
                    setSelectedIndustry(value === '__all__' ? '' : value);
                    setPage(0);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All industries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All industries</SelectItem>
                    {INDUSTRY_PRESETS.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mentors Only Toggle */}
              <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                <Label htmlFor="mentors-only" className="cursor-pointer">
                  Open to Matchups
                </Label>
                <Switch
                  id="mentors-only"
                  checked={mentorsOnly}
                  onCheckedChange={(value) => {
                    setMentorsOnly(value);
                    setPage(0);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isError ? (
        <div className="text-center py-12">
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Unable to load members.'}
          </p>
        </div>
      ) : filteredProfiles && filteredProfiles.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>
              {totalCount} member
              {totalCount !== 1 ? 's' : ''}
              {activeFiltersCount > 0 && ' matching filters'}
            </span>
            <span>
              Page {page + 1} of {totalPages}
            </span>
          </div>
          <div className="grid gap-3">
            {filteredProfiles.map((profile) => (
              <MemberCard key={profile.id} profile={profile} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">
            {searchQuery || activeFiltersCount > 0
              ? 'No members found matching your criteria.'
              : 'No members yet.'}
          </p>
          {activeFiltersCount > 0 && (
            <Button
              variant="link"
              onClick={clearFilters}
              className="mt-2"
            >
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
